/**
 * <notify-me-dialog> — custom back-in-stock / pre-launch signup modal.
 *
 * Opens when a "Notify me" CTA (`[data-notify-open]`, rendered in buy-buttons)
 * is clicked, preselecting the variant from the CTA's data-variant-id. On
 * submit it calls the Amp Back in Stock global directly:
 *
 *   window.BIS.create(email, variantId, productId, { accepts_marketing })
 *
 * The element wraps the theme's <dialog-component>, so it relies on that
 * component's showDialog()/closeDialog() for open/close + scroll lock.
 */
class NotifyMeDialog extends HTMLElement {
  connectedCallback() {
    this.scope = this.closest('.shopify-section') || document;
    this.dialogComponent = this.querySelector('dialog-component');
    this.form = this.querySelector('[data-notify-form]');
    this.select = this.querySelector('[data-notify-variant]');
    this.email = this.querySelector('[data-notify-email]');
    this.submit = this.querySelector('[data-notify-submit]');
    this.error = this.querySelector('[data-notify-error]');

    this.onOpenClick = this.onOpenClick.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    // The CTA lives outside this element (inside the product form), so listen
    // at the section level and match the opener.
    this.scope.addEventListener('click', this.onOpenClick);
    this.form?.addEventListener('submit', this.onSubmit);
  }

  disconnectedCallback() {
    this.scope?.removeEventListener('click', this.onOpenClick);
  }

  onOpenClick(event) {
    const opener = event.target.closest('[data-notify-open]');
    if (!opener) return;

    // Only respond to openers for this product (combined listings render one
    // dialog per product).
    const productId = this.dataset.productId;
    const ctaProductId = opener.closest('[data-product-id]')?.dataset.productId;
    if (productId && ctaProductId && productId !== ctaProductId) return;

    event.preventDefault();

    const variantId = opener.getAttribute('data-variant-id');
    if (variantId && this.select?.querySelector(`option[value="${variantId}"]`)) {
      this.select.value = variantId;
    }

    this.reset();
    this.dialogComponent?.showDialog?.();
    requestAnimationFrame(() => this.email?.focus());
  }

  reset() {
    this.submit?.removeAttribute('data-state');
    this.error?.setAttribute('hidden', '');
    this.email?.removeAttribute('aria-invalid');
  }

  async onSubmit(event) {
    event.preventDefault();

    if (!this.email?.checkValidity()) {
      this.email?.setAttribute('aria-invalid', 'true');
      this.email?.focus();
      return;
    }
    this.email.removeAttribute('aria-invalid');

    if (typeof window.BIS === 'undefined' || typeof window.BIS.create !== 'function') {
      // Amp app embed missing/blocked — surface an error rather than failing silently.
      this.showError();
      return;
    }

    const email = this.email.value;
    const variantId = this.select?.value;
    const productId = this.dataset.productId;

    this.submit?.setAttribute('data-state', 'sending');
    this.error?.setAttribute('hidden', '');

    try {
      await window.BIS.create(email, variantId, productId, { accepts_marketing: 'true' });
      this.submit?.setAttribute('data-state', 'done');
      window.setTimeout(() => {
        this.dialogComponent?.closeDialog?.();
        this.form?.reset();
        this.reset();
      }, 1400);
    } catch (error) {
      console.error('[notify-me-dialog] BIS.create failed', error);
      this.showError();
    }
  }

  showError() {
    this.submit?.removeAttribute('data-state');
    this.error?.removeAttribute('hidden');
  }
}

if (!customElements.get('notify-me-dialog')) {
  customElements.define('notify-me-dialog', NotifyMeDialog);
}
